import React, { useState, useEffect } from 'react';
import { ChevronDown, MapPin, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LocationSwitcher = ({ user, currentLocation, onLocationChange }) => {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);

  useEffect(() => {
    if (user && user.locations) {
      setLocations(user.locations);
      
      // Set initial location
      if (currentLocation) {
        const loc = user.locations.find(l => l.id === currentLocation);
        setSelectedLocation(loc);
      } else if (user.locations.length > 0) {
        // Default to first location
        setSelectedLocation(user.locations[0]);
        onLocationChange(user.locations[0].id);
      }
    }
  }, [user, currentLocation]);

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    onLocationChange(location.id);
  };

  // Always show location info - dropdown if multiple locations, static display if single
  if (!user) {
    return null;
  }
  
  // Show error state if user has no locations assigned
  if (!locations || locations.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 bg-red-50 rounded-lg border border-red-200">
        <MapPin className="h-4 w-4" />
        <span className="font-medium">No Location Assigned</span>
      </div>
    );
  }

  // Show static location name for single-location non-super-admin users
  if (locations.length === 1 && !user.is_super_admin) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-50 rounded-lg">
        <MapPin className="h-4 w-4" />
        <span className="font-medium">{selectedLocation?.name || locations[0]?.name || 'Location'}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors">
        <MapPin className="h-4 w-4 text-gray-500" />
        <span className="max-w-[150px] truncate">
          {selectedLocation?.name || 'Select Location'}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase">
          {user.is_super_admin ? 'All Locations' : 'Your Locations'}
        </div>
        {locations.map((location) => (
          <DropdownMenuItem
            key={location.id}
            onClick={() => handleLocationSelect(location)}
            className="flex items-center justify-between cursor-pointer"
          >
            <div className="flex flex-col">
              <span className="font-medium">{location.name}</span>
              {location.city && location.state && (
                <span className="text-xs text-gray-500">
                  {location.city}, {location.state}
                </span>
              )}
            </div>
            {selectedLocation?.id === location.id && (
              <Check className="h-4 w-4 text-blue-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LocationSwitcher;
